import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { EASE, SectionHeading } from './primitives';
import { WORKFLOW_STAGES } from './workflowStages';

const DURATION = 7000;

export function WorkflowStory() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { margin: '-35% 0px' });
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto || reduced || !inView) return;
    const id = window.setTimeout(() => setActive(a => (a + 1) % WORKFLOW_STAGES.length), DURATION);
    return () => window.clearTimeout(id);
  }, [active, auto, reduced, inView]);

  const stage = WORKFLOW_STAGES[active];
  const details = [['What the agent did', stage.did], ['What it recorded', stage.recorded], ['What the recruiter sees', stage.sees], ['What the recruiter can override', stage.override]];

  return (
    <section ref={ref} id="workflow-demo" className="relative scroll-mt-20 overflow-hidden bg-bv-ink py-24 text-white lg:py-32" aria-labelledby="workflow-title">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="bv-grid-dark absolute inset-0" />
        <div className="absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-[hsl(var(--blue)/0.25)] blur-[120px]" />
        <div className="absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[hsl(var(--hero-highlight-end)/0.18)] blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading tone="dark" id="workflow-title" eyebrow="How the agent works" title={<>Watch the agent do five steps <span className="bv-text-gradient-dark">a recruiter used to do</span></>} description="From application to review-ready evidence. Select any stage to see what the agent did, what it recorded, and exactly where your recruiter stays in control." />

        <div className="mt-14 grid gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
          <div role="tablist" aria-label="Agent workflow stages" className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-2 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0">
            {WORKFLOW_STAGES.map((item, i) => {
              const selected = i === active;
              return (
                <button
                  key={item.name}
                  type="button"
                  role="tab"
                  id={`workflow-tab-${i}`}
                  aria-selected={selected}
                  aria-controls="workflow-panel"
                  onClick={() => { setActive(i); setAuto(false); }}
                  className={cn('group relative shrink-0 overflow-hidden rounded-2xl border px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue-bright lg:w-full', selected ? 'border-white/15 bg-white/[0.08]' : 'border-transparent hover:bg-white/[0.04]')}
                >
                  <div className="flex items-center gap-3">
                    <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors', selected ? 'bv-btn-gradient text-white shadow-bv-action' : 'bg-white/[0.06] text-white/60 group-hover:text-white')}><item.icon className="h-[18px] w-[18px]" /></span>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">Step 0{i + 1}</p>
                      <p className={cn('whitespace-nowrap font-semibold', selected ? 'text-white' : 'text-white/70')}>{item.name}</p>
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {selected && (
                      <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} className="hidden overflow-hidden text-sm leading-6 text-white/60 lg:block">
                        <span className="block pt-2">{item.title}</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                  {selected && auto && inView && !reduced && (
                    <motion.span key={`progress-${active}`} aria-hidden className="bv-btn-gradient absolute bottom-0 left-0 h-[2px]" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: DURATION / 1000, ease: 'linear' }} />
                  )}
                </button>
              );
            })}
          </div>

          <div id="workflow-panel" role="tabpanel" aria-labelledby={`workflow-tab-${active}`} className="rounded-[30px] border border-white/10 bg-white/[0.04] p-2 backdrop-blur">
            <div className="grid overflow-hidden rounded-[24px] bg-white text-bv-ink xl:grid-cols-[1.08fr_1fr]">
              <div className="relative flex min-h-[380px] items-center justify-center overflow-hidden bg-bv-blue-wash p-8 sm:p-12">
                <div aria-hidden className="bv-hero-grid absolute inset-0 opacity-70" />
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div key={active} className="relative w-full" initial={reduced ? false : { opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.98 }} transition={{ duration: 0.45, ease: EASE }}>
                    <stage.Visual />
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="p-6 sm:p-9">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div key={active} initial={reduced ? false : { opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.35, ease: EASE }}>
                    <p className="text-[13px] font-semibold text-bv-coral">Step 0{active + 1} · {stage.name}</p>
                    <h3 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-[-0.02em] sm:text-[28px]">{stage.title}</h3>
                    <dl className="mt-6 divide-y divide-bv-blue-line border-t border-bv-blue-line">
                      {details.map(([term, detail]) => (
                        <div key={term} className="py-4">
                          <dt className="text-[12px] font-semibold uppercase tracking-wider text-bv-mute">{term}</dt>
                          <dd className="mt-1.5 text-[16px] leading-7 text-bv-ink">{detail}</dd>
                        </div>
                      ))}
                    </dl>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
        {/* Every stage in the document (tab content), so all five stay readable and indexable. */}
        <div hidden>
          <ol>
            {WORKFLOW_STAGES.map((item, i) => (
              <li key={item.name}>
                <h3>Step 0{i + 1}: {item.name} — {item.title}</h3>
                <dl>
                  <dt>What the agent did</dt><dd>{item.did}</dd>
                  <dt>What it recorded</dt><dd>{item.recorded}</dd>
                  <dt>What the recruiter sees</dt><dd>{item.sees}</dd>
                  <dt>What the recruiter can override</dt><dd>{item.override}</dd>
                </dl>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
